import { Archive, Lock, LockOpen, Unarchive } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import React, { FC, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useProjectRestriction } from '@client/store/project';
import { ElementKind, IProjectInfo } from '@shared/models/project.model';
import { useUserIsOwner, useUserLookup } from '@client/hooks/use-user.hooks';
import { IProjectFolder } from '@shared/models/folder.model';
import { useMouseEventCallback } from '@client/hooks/use-events.hooks';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { useProjectAction } from './lists/ProjectListItem/use-project-action.hook';

type RestrictProjectButtonProps = {
  hover: boolean;
  project: IProjectInfo | IProjectFolder;
};

type RestrictProjectTooltipOptions = {
  archived: boolean | undefined;
  locked: boolean | undefined;
  isOwner: boolean;
  /** Raw owner display name. Blank and unknown names stay out of the tooltip. */
  ownerName: string | undefined;
};

/**
 * Owner id on a list row. Folders have no owner, so a locked folder cannot name one.
 * {@link IProjectInfo}
 */
const getProjectOwnerId = (
  project: IProjectInfo | IProjectFolder,
): string | undefined => {
  switch (project.kind) {
    case ElementKind.ProjectInfo:
      return project.owner;
    case ElementKind.ProjectFolder:
      return undefined;
    default:
      return assertUnreachable(project);
  }
};

/**
 * Tooltip for the lock and archive control.
 * A non-owner sees who locked the project when that name is known.
 * {@link RestrictProjectTooltipOptions}
 */
const getRestrictProjectTooltipTitle = ({
  archived,
  locked,
  isOwner,
  ownerName,
}: RestrictProjectTooltipOptions): string => {
  if (archived) {
    return isOwner ? 'Unarchive' : 'Project archived by another user';
  }
  if (!locked) return 'Lock so that only you can edit';
  if (isOwner) return 'Unlock';

  const normalizedOwnerName = ownerName?.trim();
  if (guards.is.nonEmptyString(normalizedOwnerName)) {
    return `Locked by ${normalizedOwnerName}`;
  }
  return 'Project locked by another user';
};

const RestrictProjectButton: FC<RestrictProjectButtonProps> = ({
  hover,
  project,
}) => {
  const { id, archived, locked, name: projectName } = project;
  const restrictProject = useProjectRestriction();
  const projectAction = useProjectAction(Number(id));

  const projectId = Number(id);
  const isOwner = useUserIsOwner(projectId);
  const usersLookup = useUserLookup();
  const ownerId = getProjectOwnerId(project);
  const ownerName = ownerId ? usersLookup[ownerId]?.name : undefined;
  const { classes, cx } = useStyles({
    dimmed: !!archived || (!!locked && !isOwner),
  });

  const tooltipTitle = useMemo(
    () =>
      getRestrictProjectTooltipTitle({
        archived,
        locked,
        isOwner,
        ownerName,
      }),
    [archived, isOwner, locked, ownerName],
  );

  const handleLockProject = useMouseEventCallback(() =>
    restrictProject(projectId, projectName, { lock: true }),
  );

  const handleUnlockProject = useMouseEventCallback(() =>
    restrictProject(projectId, projectName, { lock: false }),
  );

  const handleUnarchiveProject = projectAction(
    (validProjectId) =>
      restrictProject(validProjectId, projectName, { archive: false }, true),
    'unarchive-project',
  );

  const icon = useMemo(() => {
    const styles = cx(classes.icon, isOwner && classes.enabled);

    if (archived) {
      return isOwner ? (
        <Unarchive className={styles} onClick={handleUnarchiveProject} />
      ) : (
        <Archive className={styles} />
      );
    }
    if (locked) {
      return <Lock className={styles} onClick={handleUnlockProject} />;
    }
    if (isOwner && hover) {
      return <LockOpen className={styles} onClick={handleLockProject} />;
    }
  }, [
    classes,
    cx,
    handleLockProject,
    handleUnarchiveProject,
    handleUnlockProject,
    hover,
    isOwner,
    locked,
    archived,
  ]);

  return (
    <Tooltip arrow={false} placement="top" title={tooltipTitle}>
      {icon ? <div className={classes.container}>{icon}</div> : <div></div>}
    </Tooltip>
  );
};

const useStyles = makeStyles<{ dimmed: boolean }>()(
  ({ palette }, { dimmed }) => ({
    container: {
      display: 'flex',
      justifyContent: 'center',
      flex: 1,
    },
    icon: {
      fill: dimmed ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.8)',
      fontSize: '1rem',
    },
    enabled: {
      cursor: 'pointer',
      '&:hover': {
        fill: palette.text.primary,
      },
    },
  }),
);

export default RestrictProjectButton;
